// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo } from 'react';
import { useGlobalSearchParams } from 'expo-router';

import { useUser } from '@/app/user-context';
import type { AccessCredentials } from '@/services/claimHttp';

/** Query params that keep viewer mode alive across a navigation. Empty when not spectating. */
export type ViewerRouteParams = { inviteToken?: string; viewer?: string; results?: string };

export type InviteViewer = {
  isInviteViewer: boolean;
  /**
   * Opened from a shared results link (`results=1`): a spectator who wants the numbers and nothing
   * else. The layout drops its menus and the pages their back/share/picker controls for them.
   * Implies isInviteViewer.
   */
  isResultsView: boolean;
  inviteToken: string;
  /** Credentials for appendClaimQuery(). Null unless spectating. */
  accessSession: AccessCredentials | null;
  /** False for every spectator, signed in or not. Screens AND this must agree. */
  canWrite: boolean;
  /** Spread into a route's params (or withViewerParams for a path string) on every push. */
  viewerRouteParams: ViewerRouteParams;
};

const firstParam = (value: string | string[] | undefined): string =>
  typeof value === 'string'
    ? value.trim()
    : Array.isArray(value)
      ? String(value[0] ?? '').trim()
      : '';

/**
 * The pure half of {@link useInviteViewer}, exported for tests (mirrors resolveEffectiveRoles).
 *
 * Viewer mode is driven by the user's explicit choice (`viewer=1`), NOT by whether they happen to
 * be signed out. Deriving it from auth state meant a signed-in user who picked "watch" still got
 * the full scoring UI, and a signed-out one with a leftover claim session did too.
 */
export const resolveInviteViewer = (
  inviteTokenParam: string | string[] | undefined,
  viewerParam: string | string[] | undefined,
  userToken: string | null | undefined,
  resultsParam?: string | string[] | undefined,
): InviteViewer => {
  const inviteToken = firstParam(inviteTokenParam);
  const isResultsView = firstParam(resultsParam) === '1';
  const viewerRequested = firstParam(viewerParam) === '1' || isResultsView;
  // Anonymous + an invite token is implicitly spectating: there is nothing else such a visitor
  // could be doing on these screens, and older links carry no viewer flag.
  const isInviteViewer = viewerRequested || (!userToken && !!inviteToken);

  return {
    isInviteViewer,
    isResultsView,
    inviteToken,
    accessSession: isInviteViewer && inviteToken ? { inviteToken } : null,
    canWrite: !isInviteViewer && !!userToken,
    viewerRouteParams: isInviteViewer
      ? { ...(inviteToken ? { inviteToken } : {}), viewer: '1', ...(isResultsView ? { results: '1' } : {}) }
      : {},
  };
};

/**
 * "Am I spectating, and may I write?" — the seam every read-only invite screen shares.
 *
 * A viewer opened a tour invite link and chose to watch instead of claiming a player. They are
 * deliberately NOT a player: nothing is created server-side and nothing is persisted on the
 * device, so the URL carries the whole state. Reading it through useGlobalSearchParams (the same
 * trick useAuthFlow uses for its route bypass) makes it visible on every screen without threading
 * props — but expo-router does not propagate query params on its own, so each push has to
 * re-append viewerRouteParams or the mode silently ends.
 */
export const useInviteViewer = (): InviteViewer => {
  const { user } = useUser();
  const { inviteToken: inviteTokenParam, viewer: viewerParam, results: resultsParam } =
    useGlobalSearchParams<{ inviteToken?: string | string[]; viewer?: string | string[]; results?: string | string[] }>();

  return useMemo(
    () => resolveInviteViewer(inviteTokenParam, viewerParam, user?.token, resultsParam),
    [inviteTokenParam, viewerParam, resultsParam, user?.token],
  );
};

export default useInviteViewer;
